/**
 * AgentLiveBroadcastModal.tsx
 *
 * Real WebRTC broadcast for an AI agent (Phase 2 / slice 4e).
 *
 * The operator (the human wallet currently connected to GeoTrust) is
 * the WebRTC producer; the agent's pubkey is the broadcaster id, so
 * the room shows up under the agent's identity. The operator pays
 * for the paid live session (`paidLiveSessionService.requestSession`
 * already accepts `wallet !== broadcasterId`); the agent's secret key
 * never leaves the MCP server.
 *
 * Lifecycle:
 *
 *   1. Mount AgentBroadcastCanvas → produces a synthetic
 *      MediaStream (Ken Burns avatar + TTS-mixed audio track).
 *   2. requestLiveSession({ wallet: operatorWallet, country,
 *      broadcasterId: agentPk }) → grantToken.
 *      - If requiresPayment is true we surface a clear message
 *        instead of trying to drive the wallet flow inline; that
 *        integration is slice 4f. The operator can use the
 *        existing "Go Live" path on their profile to cover the
 *        session cost first if needed.
 *   3. startBroadcast(engine.getStream(), { broadcasterId: agentPk,
 *      liveSessionId, grantToken, onAgentCaption: ... }).
 *   4. The MCP server's `agent_speak({ text, broadcast: true })`
 *      tool fans rendered captions into the room. The
 *      onAgentCaption handler decodes the audio_base64 and calls
 *      engine.pushCaption({ text, audio, durationMs }) so the
 *      caption text shows on the canvas and the audio plays through
 *      the synthetic audio track every viewer is consuming.
 *   5. End broadcast → controller.stop() + engine.dispose() +
 *      stopLiveSession(sessionId).
 *
 * NOT implemented in this slice (called out as TODO 4f):
 *
 *   - Inline wallet payment when requiresPayment is true.
 *   - Recording owner re-keyed to broadcasterId so the agent's
 *     profile gets the VOD instead of the operator's.
 */

import React, { useCallback, useEffect, useRef, useState } from 'react';
import AgentBroadcastCanvas, {
  useAgentBroadcastEngine,
  useAgentBroadcastStats,
  type AgentBroadcastEngine,
  type AgentBroadcastEngineStats,
} from './AgentBroadcastCanvas';
import {
  startBroadcast,
  type BroadcastController,
  type BroadcastStats,
  type AgentCaptionEvent,
} from '../services/webrtcBroadcastService';
import {
  requestLiveSession,
  stopLiveSession,
  confirmLivePayment,
  markLivePaymentFailed,
  speakAsAgentDuringLive,
  type ApproxLocation,
} from '../services/liveSessionService';
import {
  pushTestCaption,
  getSpeechStatus,
  type SpeechStatus,
} from '../services/agentBroadcastService';
import { getResidencySnapshot } from '../services/citizenshipService';
import { iso2ToNumeric } from '../countryCodes';
import type { ExecuteLivePayment } from './ProfileOverlay';

interface Props {
  agentPublicKey: string;
  displayName: string | null;
  avatarUrl: string | null;
  /** Connected operator wallet — pays for the session. */
  operatorWallet: string | null;
  /**
   * Optional country override. When omitted, the modal resolves the agent's
   * citizenship country on open. When the host passes the **viewer's**
   * policy country (map/GPS), that wins — required for operators who
   * registered their wallet as the agent without separate citizenship.
   */
  country?: number | null;
  /** Quantised location attached to the paid-session request (same as human live). */
  approxLocation?: ApproxLocation;
  /**
   * Inline wallet payment handler. Wired by App.tsx via
   * `createLiveSessionPaymentHandler({ gWalletSigner })`. When
   * the backend says `requiresPayment === true`, this is what
   * drives the operator's wallet to send the session cost and
   * returns a payment receipt (Stellar tx hash).
   *
   * If unset, the modal surfaces a clear "payment required but no
   * handler is wired" message instead of trying to half-implement
   * payment inline (matches the human-broadcaster fallback in
   * ProfileOverlay).
   */
  onExecuteLivePayment?: ExecuteLivePayment;
  open: boolean;
  onClose: () => void;
}

type Phase =
  | 'idle'
  | 'requesting'
  | 'payment_required'
  | 'paying'
  | 'confirming'
  | 'starting'
  | 'live'
  | 'ending'
  | 'ended'
  | 'error';

const AgentLiveBroadcastModal: React.FC<Props> = ({
  agentPublicKey,
  displayName,
  avatarUrl,
  operatorWallet,
  country,
  approxLocation,
  onExecuteLivePayment,
  open,
  onClose,
}) => {
  const { engine } = useAgentBroadcastEngine({
    agentPublicKey,
    displayName,
    avatarUrl,
  });

  // Polled engine stats so we can render a "canvas alive" indicator.
  // Exposes tickCount + lastTickAt so a black-screen scenario (engine
  // disposed mid-broadcast, e.g. StrictMode double-mount before the
  // fix) is visible to the operator instead of silently degrading.
  const engineStats = useAgentBroadcastStats(engine, 1000);

  // Polled SFU broadcaster stats so we can render a "Sending" badge
  // showing transport state + outbound bytes. Critical for diagnosing
  // the case where the canvas is alive locally but no media reaches
  // viewers — without this, a viewer-side ICE failure looks identical
  // to a broadcaster-side one and the operator has no way to tell
  // whether to retry locally or ask the viewer to switch networks.
  const [broadcastStats, setBroadcastStats] = useState<BroadcastStats | null>(null);

  const [phase, setPhase] = useState<Phase>('idle');
  const [errMsg, setErrMsg] = useState<string | null>(null);
  const [sessionId, setSessionId] = useState<string | null>(null);
  /**
   * Live-session HMAC grant issued by `requestLiveSession` /
   * `confirmLivePayment`. Held in state (not just a ref) so the
   * "Speak as agent" button can render its disabled state correctly
   * before the session reaches the `live` phase. The token never
   * leaves the modal — it goes straight to the operator-driven
   * `/api/live-sessions/:id/agent-speak` endpoint below.
   */
  const [grantToken, setGrantToken] = useState<string | null>(null);
  const [viewerCount, setViewerCount] = useState(0);
  const [captionLog, setCaptionLog] = useState<AgentCaptionEvent[]>([]);
  const [speechStatus, setSpeechStatus] = useState<SpeechStatus | null>(null);
  const [testText, setTestText] = useState('');
  const [speakBusy, setSpeakBusy] = useState(false);
  const [speakErr, setSpeakErr] = useState<string | null>(null);

  /**
   * Resolved broadcast country (numeric ISO-3166-1). When the `country`
   * prop is provided we use that first (viewer / host policy country).
   * Otherwise we read geo-citizenship for the agent on open.
   */
  const [resolvedCountry, setResolvedCountry]   = useState<number | null>(country ?? null);
  const [residencyIso2,   setResidencyIso2]     = useState<string | null>(null);
  const [residencyError,  setResidencyError]    = useState<string | null>(null);

  /** Last payment quote from the backend (kept for the confirming-payment label). */
  const [paymentQuote, setPaymentQuote] = useState<{
    sessionCost: string;
    sessionCostXlm: string;
  } | null>(null);

  const controllerRef = useRef<BroadcastController | null>(null);

  // Speech status (read-only diagnostic).
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    getSpeechStatus()
      .then((s) => { if (!cancelled) setSpeechStatus(s); })
      .catch(() => { if (!cancelled) setSpeechStatus(null); });
    return () => { cancelled = true; };
  }, [open]);

  // Auto-resolve the agent's residency country on open (slice 4f-a).
  useEffect(() => {
    if (!open) return;
    if (country != null) {
      setResolvedCountry(country);
      return;
    }
    let cancelled = false;
    setResidencyError(null);
    getResidencySnapshot(agentPublicKey)
      .then((snap) => {
        if (cancelled) return;
        const iso2 = (snap?.country || '').trim().toUpperCase() || null;
        setResidencyIso2(iso2);
        if (iso2) {
          const num = iso2ToNumeric(iso2);
          setResolvedCountry(num > 0 ? num : null);
        }
      })
      .catch((err: Error) => {
        if (!cancelled) setResidencyError(err.message);
      });
    return () => { cancelled = true; };
  }, [open, country, agentPublicKey]);

  // Caption decode + push helper (used by both onAgentCaption and the
  // test-caption button).
  const handleAgentCaption = useCallback(async (caption: AgentCaptionEvent) => {
    setCaptionLog((prev) => [...prev.slice(-19), caption]);
    try {
      const blob = base64ToBlob(caption.audio_base64, caption.mime || 'audio/mpeg');
      await engine.pushCaption({
        text:       caption.text,
        audio:      blob,
        durationMs: caption.duration_ms,
      });
    } catch (e) {
      // Fall back to caption-only.
      try {
        await engine.pushCaption({ text: caption.text, durationMs: caption.duration_ms });
      } catch { /* ignore */ }
       
    }
  }, [engine]);

  // Stop / cleanup. Idempotent.
  const stop = useCallback(async () => {
    setPhase('ending');
    try {
      controllerRef.current?.stop();
    } catch { /* ignore */ }
    controllerRef.current = null;
    if (sessionId) {
      try { await stopLiveSession(sessionId); } catch { /* server cleans up on socket close anyway */ }
    }
    setSessionId(null);
    setGrantToken(null);
    setViewerCount(0);
    setPhase('ended');
  }, [sessionId]);

  // Helper: start the actual WebRTC broadcast given a session + grant.
  // Used by both the no-payment-required path and the post-payment
  // confirmation path.
  const _startBroadcast = useCallback((sessId: string, sessGrantToken: string) => {
    setSessionId(sessId);
    setGrantToken(sessGrantToken);
    setPhase('starting');
    try {
      const stream = engine.getStream();
      const ctrl = startBroadcast(stream, {
        broadcasterId:   agentPublicKey,
        wallet:          operatorWallet || undefined,
        country:         resolvedCountry == null ? undefined : Number(resolvedCountry),
        liveSessionId:   sessId,
        grantToken:      sessGrantToken,
        onViewersUpdate: (n) => setViewerCount(n),
        onAgentCaption:  (caption) => { void handleAgentCaption(caption); },
        onError:         (err) => {
          setErrMsg(err.message);
          setPhase('error');
        },
        onSessionEnded:  (reason) => {
          setErrMsg(`Session ended: ${reason}`);
          setPhase('ended');
        },
      });
      controllerRef.current = ctrl;
      setPhase('live');
    } catch (e) {
      setErrMsg(`Failed to start broadcast: ${(e as Error).message}`);
      setPhase('error');
    }
  }, [agentPublicKey, engine, handleAgentCaption, operatorWallet, resolvedCountry]);

  /**
   * Speak as the agent over the live broadcast.
   *
   * Synthesises the operator-typed text via Azure TTS on the backend
   * and fans the rendered caption + audio into the agent's WebRTC room.
   * Every viewer hears the audio mixed into the broadcast and sees the
   * caption text; the operator's own broadcast preview canvas re-uses
   * the existing `live_agent_caption` handler so the same caption /
   * audio appears here too without a separate local push path.
   */
  const handleSpeakLive = useCallback(async () => {
    const text = testText.trim();
    if (!text) return;
    if (!sessionId || !grantToken) {
      setSpeakErr('Not live yet — start the broadcast first.');
      return;
    }
    setSpeakErr(null);
    setSpeakBusy(true);
    try {
      const result = await speakAsAgentDuringLive(sessionId, { grantToken, text });
      // The fan-out routes through the WebRTC hub; the broadcaster
      // also receives the `live_agent_caption` event, so the existing
      // `handleAgentCaption` handler will push the caption + audio
      // into the engine. Nothing else to do locally — the input gets
      // cleared so the operator can type the next line.
      setTestText('');
      if (result.broadcast && result.broadcast.sent === false && result.broadcast.reason) {
        setSpeakErr(`Synthesised but fan-out did not reach viewers (${result.broadcast.reason}).`);
      }
    } catch (e: unknown) {
      const err = e as Error & { payload?: { error?: string } };
      const reason = err?.payload?.error || err?.message || 'speak_failed';
      setSpeakErr(`Speak failed: ${reason}`);
    } finally {
      setSpeakBusy(false);
    }
  }, [sessionId, grantToken, testText]);

  // Start a real broadcast (slice 4f-b).
  const goLive = useCallback(async () => {
    if (!operatorWallet) {
      setErrMsg('Connect your wallet first — the operator wallet pays for the live session.');
      setPhase('error');
      return;
    }
    setErrMsg(null);
    setPhase('requesting');

    let req: Awaited<ReturnType<typeof requestLiveSession>>;
    try {
      req = await requestLiveSession({
        wallet:        operatorWallet,
        broadcasterId: agentPublicKey,
        country:       resolvedCountry == null ? undefined : Number(resolvedCountry),
        approxLocation,
      });
    } catch (e: unknown) {
      const err = e as Error & { payload?: { reason?: string } };
      const reason = err?.payload?.reason || err?.message || '';
      let detail = reason;
      if (reason === 'webrtc_policy_not_set') {
        detail =
          'WebRTC policy for this country is not configured on the server yet (no silent default). ' +
          'Ask an admin to enable live sessions for your region in compliance / WebRTC policy, or try again when your viewer country resolves to one that is already configured.';
      }
      setErrMsg(`Failed to request live session: ${detail}`);
      setPhase('error');
      return;
    }

    setSessionId(req.session?.id || null);

    if (!req.requiresPayment) {
      _startBroadcast(req.session.id, req.grantToken);
      return;
    }

    // ── Payment required ────────────────────────────────────────────────
    const xlmDisplay = stroopsToXlm(req.sessionCost);
    setPaymentQuote({ sessionCost: req.sessionCost, sessionCostXlm: xlmDisplay });

    if (!onExecuteLivePayment) {
      try {
        await markLivePaymentFailed(req.session.id, 'payment_handler_missing');
      } catch { /* the backend will time the session out anyway */ }
      setErrMsg(
        `Agent broadcasts in this country cost ${xlmDisplay} XLM. ` +
        `No wallet payment handler is wired into this build — open a regular live broadcast ` +
        `on your profile to confirm the wallet flow works, then retry.`,
      );
      setPhase('payment_required');
      return;
    }

    setPhase('paying');
    let receipt: { paymentReference: string; paidAmount?: string };
    try {
      receipt = await onExecuteLivePayment({
        sessionId:     req.session.id,
        country:       resolvedCountry == null ? null : Number(resolvedCountry),
        wallet:        operatorWallet,
        amountStroops: req.sessionCost,
        amountDisplay: xlmDisplay,
      });
    } catch (e) {
      const msg = (e as Error)?.message || 'payment_failed';
      try { await markLivePaymentFailed(req.session.id, msg); } catch { /* ignore */ }
      setErrMsg(`Payment failed: ${msg}`);
      setPhase('error');
      return;
    }

    setPhase('confirming');
    try {
      const confirmed = await confirmLivePayment(req.session.id, {
        paymentReference: receipt.paymentReference,
        paidAmount:       receipt.paidAmount || req.sessionCost,
      });
      _startBroadcast(confirmed.session.id, confirmed.grantToken);
    } catch (e) {
      setErrMsg(`Failed to confirm payment: ${(e as Error).message}`);
      setPhase('error');
    }
  }, [
    _startBroadcast,
    agentPublicKey,
    onExecuteLivePayment,
    operatorWallet,
    resolvedCountry,
    approxLocation,
  ]);

  // Auto-stop when the modal closes while live.
  useEffect(() => {
    if (!open && controllerRef.current) {
      void stop();
    }
  }, [open, stop]);

  // Poll the broadcast controller's send-side stats. Only runs while a
  // controller exists (i.e. between the `live` phase starting and
  // `stop()` clearing controllerRef), and only while the modal is
  // open so closed-but-mounted instances don't pay the cost.
  useEffect(() => {
    if (!open || (phase !== 'live' && phase !== 'starting')) {
      setBroadcastStats(null);
      return;
    }
    const tick = () => {
      const ctrl = controllerRef.current;
      if (!ctrl || typeof ctrl.getStats !== 'function') {
        setBroadcastStats(null);
        return;
      }
      try { setBroadcastStats(ctrl.getStats()); } catch { /* ignore */ }
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => { clearInterval(id); };
  }, [open, phase]);

  if (!open) return null;

  const isLive = phase === 'live';

  return (
    <div style={overlayStyle} role="dialog" aria-modal="true" aria-label="Agent live broadcast">
      <div style={panelStyle}>
        <div style={headerStyle}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <span style={{ fontSize: 18 }}>{isLive ? '🔴' : '🤖'}</span>
            <div style={{ minWidth: 0 }}>
              <div style={{ fontSize: 12, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: 1 }}>
                {phaseLabel(phase)}
              </div>
              <div style={{ fontSize: 16, fontWeight: 600, color: '#f9fafb' }}>
                {displayName || `${agentPublicKey.slice(0, 8)}…`}
              </div>
            </div>
          </div>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            {isLive && (
              <span style={liveBadgeStyle}>LIVE • {viewerCount} viewer{viewerCount === 1 ? '' : 's'}</span>
            )}
            <button onClick={onClose} aria-label="Close" style={closeBtnStyle}>×</button>
          </div>
        </div>

        <div style={canvasWrapStyle}>
          <AgentBroadcastCanvas engine={engine} />
        </div>

        <div style={controlsStyle}>
          {!isLive && phase !== 'ended' && (
            <button
              type="button"
              onClick={() => void goLive()}
              disabled={
                phase === 'requesting' ||
                phase === 'paying' ||
                phase === 'confirming' ||
                phase === 'starting' ||
                phase === 'payment_required'
              }
              style={primaryBtnStyle}
            >
              {phase === 'requesting'  ? 'Requesting session…'
                : phase === 'paying'   ? `Awaiting wallet payment${paymentQuote ? ` (${paymentQuote.sessionCostXlm} XLM)` : '…'}`
                : phase === 'confirming' ? 'Confirming payment…'
                : phase === 'starting'   ? 'Starting broadcast…'
                : '🔴 Go live as agent'}
            </button>
          )}

          {isLive && (
            <button
              type="button"
              onClick={() => void stop()}
              style={dangerBtnStyle}
            >
              End broadcast
            </button>
          )}

          {phase === 'ended' && (
            <button
              type="button"
              onClick={() => { setPhase('idle'); setErrMsg(null); }}
              style={primaryBtnStyle}
            >
              Reset
            </button>
          )}

          <div style={{ display: 'flex', gap: 8, marginTop: 6 }}>
            <input
              value={testText}
              onChange={(e) => setTestText(e.target.value.slice(0, 200))}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  if (isLive) void handleSpeakLive();
                  else if (testText.trim()) {
                    pushTestCaption(engine, testText.trim()).catch(() => { /* ignore */ });
                    setTestText('');
                  }
                }
              }}
              placeholder={isLive
                ? 'Type to speak as agent — viewers hear it via TTS.'
                : 'Test caption (visual preview only — no Azure cost).'}
              style={inputStyle}
              maxLength={200}
              disabled={speakBusy}
            />
            {isLive ? (
              <button
                type="button"
                onClick={() => void handleSpeakLive()}
                disabled={!testText.trim() || speakBusy || !grantToken || !speechStatus?.configured}
                style={speakLiveBtnStyle}
                title={
                  !speechStatus?.configured
                    ? 'Backend Azure TTS is not configured — viewers will not hear anything.'
                    : 'Synthesize this text via Azure TTS and broadcast it live to all viewers.'
                }
              >
                {speakBusy ? 'Speaking…' : '🔊 Speak'}
              </button>
            ) : (
              <button
                type="button"
                onClick={() => {
                  const t = testText.trim();
                  if (!t) return;
                  pushTestCaption(engine, t).catch(() => { /* ignore */ });
                  setTestText('');
                }}
                disabled={!testText.trim()}
                style={secondaryBtnStyle}
              >
                Push
              </button>
            )}
          </div>

          {speakErr && (
            <div style={errorStyle}>{speakErr}</div>
          )}

          {errMsg && (
            <div style={errorStyle}>{errMsg}</div>
          )}
        </div>

        <div style={statusBarStyle}>
          <SpeechBadge status={speechStatus} />
          <CountryBadge iso2={residencyIso2} numeric={resolvedCountry} error={residencyError} />
          <CanvasAliveBadge stats={engineStats} />
          <SendingBadge stats={broadcastStats} live={phase === 'live'} />
          {paymentQuote && (
            <span style={badgeMutedStyle}>
              cost {paymentQuote.sessionCostXlm} XLM
            </span>
          )}
          <span style={badgeMutedStyle}>
            {sessionId ? `session ${sessionId.slice(0, 12)}…` : 'no session'}
          </span>
          <span style={badgeMutedStyle}>
            {captionLog.length} caption{captionLog.length === 1 ? '' : 's'} received
          </span>
        </div>
      </div>
    </div>
  );
};

// ── Helpers ────────────────────────────────────────────────────────────────

function base64ToBlob(b64: string, mime: string): Blob {
  const bin = atob(b64);
  const bytes = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
  return new Blob([bytes], { type: mime });
}

/**
 * Stroops → display XLM (7 decimals, trims trailing zeros). Used to
 * humanise the session-cost quote without pulling stellar-sdk into
 * this component's bundle.
 */
function stroopsToXlm(stroops: string | number | null | undefined): string {
  const s = String(stroops || '0');
  if (!/^\d+$/.test(s)) return s;
  if (s === '0') return '0';
  const padded = s.padStart(8, '0');
  const intPart = padded.slice(0, padded.length - 7) || '0';
  const fracPart = padded.slice(padded.length - 7).replace(/0+$/, '');
  return fracPart ? `${intPart}.${fracPart}` : intPart;
}

function phaseLabel(phase: Phase): string {
  switch (phase) {
    case 'idle':              return 'Live broadcast';
    case 'requesting':        return 'Live broadcast • requesting';
    case 'payment_required':  return 'Live broadcast • payment required';
    case 'paying':            return 'Live broadcast • signing payment';
    case 'confirming':        return 'Live broadcast • confirming payment';
    case 'starting':          return 'Live broadcast • starting';
    case 'live':              return 'On air';
    case 'ending':            return 'Live broadcast • ending';
    case 'ended':             return 'Broadcast ended';
    case 'error':             return 'Live broadcast • error';
    default:                  return 'Live broadcast';
  }
}

const SpeechBadge: React.FC<{ status: SpeechStatus | null }> = ({ status }) => {
  if (!status) return <span style={badgeMutedStyle}>Speech: checking…</span>;
  if (!status.configured) return <span style={badgeWarnStyle}>Speech: not configured</span>;
  return <span style={badgeOkStyle}>Speech: ready ({status.region})</span>;
};

/**
 * Send-side WebRTC pipeline badge. Polls the broadcast controller's
 * `getStats()` so the operator can see whether the SFU send transport
 * actually connected and how many bytes have left the browser.
 *
 * "Sending: 0 B" while the canvas is alive is the loudest signal that
 * the broadcaster's media path is broken (almost always a TURN/UDP
 * issue); "Sending: ✓ 12 KB" while a viewer reports a black screen
 * tells you the broadcaster is healthy and the issue is on the
 * viewer's side.
 */
const SendingBadge: React.FC<{ stats: BroadcastStats | null; live: boolean }> = ({ stats, live }) => {
  if (!live) return null;
  if (!stats) return <span style={badgeMutedStyle}>Sending: starting…</span>;
  const totalBytes   = stats.videoBytesSent   + stats.audioBytesSent;
  const totalPackets = stats.videoPacketsSent + stats.audioPacketsSent;
  const human = totalBytes > 0
    ? formatBytes(totalBytes)
    : (totalPackets > 0 ? `${totalPackets.toLocaleString()} pkts` : '0 B');
  const transport = stats.transportState;
  const transportLabel = transport === 'connected' ? '✓' : transport;
  if (transport === 'failed' || transport === 'closed' || transport === 'disconnected') {
    return (
      <span style={badgeWarnStyle} title={`SFU send transport ${transport}; viewers will see a black screen.`}>
        Sending: {transport}
      </span>
    );
  }
  // We only treat the pipeline as "stalled" when both bytes AND packets
  // are zero. Some browsers (Chrome especially) don't populate
  // `bytesSent` on outbound-rtp reports for several seconds, even
  // though `packetsSent` already shows actual RTP flowing — using
  // bytes alone produced false-positive "stalled" badges while video
  // was visibly working on the viewer side. The 30s grace window also
  // gives slow networks (mobile carriers, CGNAT) more headroom before
  // we cry wolf.
  const stale = transport === 'connected' && stats.ageMs > 30_000
    && totalBytes === 0 && totalPackets === 0;
  if (stale) {
    return (
      <span
        style={badgeWarnStyle}
        title="Send transport is up but no RTP packets have been forwarded for 30s — likely a TURN/UDP block on this network. Try a wired/different network."
      >
        Sending: stalled
      </span>
    );
  }
  if (transport !== 'connected') {
    return (
      <span style={badgeMutedStyle} title={`SFU send transport state: ${transport}`}>
        Sending: {transport}
      </span>
    );
  }
  return (
    <span
      style={badgeOkStyle}
      title={`Video ${formatBytes(stats.videoBytesSent)} (${stats.videoPacketsSent.toLocaleString()} pkts) • Audio ${formatBytes(stats.audioBytesSent)} (${stats.audioPacketsSent.toLocaleString()} pkts)`}
    >
      Sending: {transportLabel} {human}
    </span>
  );
};

function formatBytes(n: number): string {
  if (!Number.isFinite(n) || n <= 0) return '0 B';
  const units = ['B', 'KB', 'MB', 'GB'];
  let i = 0;
  let v = n;
  while (v >= 1024 && i < units.length - 1) { v /= 1024; i++; }
  return `${v < 10 ? v.toFixed(1) : Math.round(v)} ${units[i]}`;
}

/**
 * Health badge for the synthetic canvas stream. The captureStream a
 * viewer sees is only as fresh as the last `_tick()` draw — if the
 * engine is disposed or stalled the picture goes black. Surfacing
 * this in the operator UI makes that condition obvious instead of
 * silently appearing as "live, 0 viewers seeing video".
 */
const CanvasAliveBadge: React.FC<{ stats: AgentBroadcastEngineStats | null }> = ({ stats }) => {
  if (!stats) {
    return <span style={badgeMutedStyle}>Canvas: starting…</span>;
  }
  if (stats.disposed) {
    return (
      <span style={badgeWarnStyle} title="The render engine has been disposed; the canvas stream is frozen.">
        Canvas: stopped
      </span>
    );
  }
  const ageMs = stats.lastTickAt
    ? Math.max(0, performance.now() - stats.lastTickAt)
    : Number.POSITIVE_INFINITY;
  const stalled = ageMs > 1500;
  if (stalled || stats.tickCount === 0) {
    return (
      <span
        style={badgeWarnStyle}
        title={`Last frame ${Number.isFinite(ageMs) ? Math.round(ageMs) + 'ms' : 'never'} ago — viewers will see a black screen.`}
      >
        Canvas: stalled
      </span>
    );
  }
  return (
    <span
      style={badgeOkStyle}
      title={`${stats.tickCount.toLocaleString()} frames rendered, last ${Math.round(ageMs)}ms ago.`}
    >
      Canvas: live ({stats.tickCount.toLocaleString()}f)
    </span>
  );
};

const CountryBadge: React.FC<{
  iso2: string | null;
  numeric: number | null;
  error: string | null;
}> = ({ iso2, numeric, error }) => {
  if (error) {
    return (
      <span style={badgeWarnStyle} title={error}>
        Country: residency unresolved
      </span>
    );
  }
  if (iso2 && numeric) {
    return <span style={badgeOkStyle} title={`Resolved from geo-citizenship.active_country()`}>{`Country: ${iso2} (${numeric})`}</span>;
  }
  if (iso2 && !numeric) {
    return (
      <span style={badgeWarnStyle} title="No numeric ISO-3166-1 mapping for this code in src/countryCodes.ts">
        {`Country: ${iso2} (unmapped)`}
      </span>
    );
  }
  if (numeric) {
    return <span style={badgeOkStyle} title="Country provided by parent component">{`Country: ${numeric}`}</span>;
  }
  return <span style={badgeMutedStyle}>Country: not set</span>;
};

// ── Inline styles ──────────────────────────────────────────────────────────

const overlayStyle: React.CSSProperties = {
  position: 'fixed', inset: 0, background: 'rgba(15, 23, 42, 0.78)', zIndex: 10_000,
  display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16,
  backdropFilter: 'blur(4px)',
};

const panelStyle: React.CSSProperties = {
  width: 'min(900px, 100%)', maxHeight: '92vh', background: '#0f172a',
  color: '#e5e7eb', borderRadius: 16, boxShadow: '0 24px 64px rgba(0, 0, 0, 0.55)',
  display: 'flex', flexDirection: 'column', overflow: 'hidden',
};

const headerStyle: React.CSSProperties = {
  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
  padding: '14px 18px', borderBottom: '1px solid #1e293b',
};

const closeBtnStyle: React.CSSProperties = {
  background: 'transparent', border: 'none', color: '#9ca3af',
  fontSize: 28, lineHeight: 1, cursor: 'pointer', padding: 4,
};

const canvasWrapStyle: React.CSSProperties = {
  padding: 16, background: '#020617', display: 'flex', justifyContent: 'center',
};

const controlsStyle: React.CSSProperties = {
  padding: '14px 18px', display: 'flex', flexDirection: 'column', gap: 10,
  borderTop: '1px solid #1e293b',
};

const inputStyle: React.CSSProperties = {
  flex: 1, background: '#020617', border: '1px solid #334155',
  borderRadius: 8, color: '#f9fafb', padding: '8px 12px', fontSize: 13,
  fontFamily: 'inherit',
};

const primaryBtnStyle: React.CSSProperties = {
  background: '#dc2626', border: '1px solid #b91c1c', color: '#fff',
  fontWeight: 700, padding: '10px 18px', borderRadius: 8, cursor: 'pointer', fontSize: 14,
};

const secondaryBtnStyle: React.CSSProperties = {
  background: '#334155', border: '1px solid #475569', color: '#fff',
  fontWeight: 600, padding: '8px 14px', borderRadius: 8, cursor: 'pointer', fontSize: 13,
};

const speakLiveBtnStyle: React.CSSProperties = {
  background: '#0ea5e9', border: '1px solid #0284c7', color: '#fff',
  fontWeight: 700, padding: '8px 14px', borderRadius: 8, cursor: 'pointer', fontSize: 13,
};

const dangerBtnStyle: React.CSSProperties = {
  background: '#7f1d1d', border: '1px solid #b91c1c', color: '#fee2e2',
  fontWeight: 600, padding: '10px 18px', borderRadius: 8, cursor: 'pointer', fontSize: 14,
};

const errorStyle: React.CSSProperties = {
  background: '#7f1d1d', border: '1px solid #b91c1c', color: '#fee2e2',
  padding: '8px 12px', borderRadius: 8, fontSize: 13, whiteSpace: 'pre-line',
};

const liveBadgeStyle: React.CSSProperties = {
  background: '#dc2626', color: '#fff', padding: '4px 10px', borderRadius: 999,
  fontWeight: 700, fontSize: 11, letterSpacing: 1,
};

const statusBarStyle: React.CSSProperties = {
  display: 'flex', alignItems: 'center', flexWrap: 'wrap',
  gap: 8, padding: '10px 18px', background: '#020617',
  borderTop: '1px solid #1e293b', fontSize: 12,
};

const badgeBase: React.CSSProperties = { padding: '4px 10px', borderRadius: 999, fontWeight: 500 };
const badgeMutedStyle: React.CSSProperties = { ...badgeBase, background: '#1e293b', color: '#94a3b8' };
const badgeOkStyle:    React.CSSProperties = { ...badgeBase, background: '#064e3b', color: '#bbf7d0' };
const badgeWarnStyle:  React.CSSProperties = { ...badgeBase, background: '#78350f', color: '#fed7aa' };

export default AgentLiveBroadcastModal;
// Suppress lint about the unused import when AgentBroadcastEngine isn't referenced
export type { AgentBroadcastEngine };
